import React, { useMemo, useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Space, Tag, Switch } from 'antd';

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [rolesOptions, setRolesOptions] = useState([]);
  const [editModal, setEditModal] = useState({ open:false, user:null });
  const [editRoles, setEditRoles] = useState([]);
  const [editStatus, setEditStatus] = useState(true);

  // 确保 window.api 全局可用，避免 undefined 报错
  useEffect(() => {
    if (!window.api) window.api = require('../api/http').default;
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await window.api.get('/users');
      if (!res.data.success) throw new Error(res.data.error || '加载失败');
      const normalized = (res.data.records || []).map(u => {
        const roles = Array.isArray(u.roles)
          ? u.roles
          : (typeof u.roles === 'string' ? u.roles.split(',').map(r => r.trim()).filter(Boolean) : []);
        const statusNum = Number(u.status ?? u.is_active ?? 0);
        const activeNum = Number(u.is_active ?? u.status ?? statusNum);
        return {
          ...u,
          roles,
          status: Number.isFinite(statusNum) ? statusNum : 0,
          is_active: Number.isFinite(activeNum) ? activeNum : 0
        };
      });
      setUsers(normalized);
    } catch (e) {
      message.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await window.api.get('/users/roles');
      if(!res.data.success) return;
      setRolesOptions((res.data.records||[]).map(r=> ({ value:r.name, label:r.name })));
    } catch(e){ /* ignore */ }
  };
  useEffect(() => { fetchUsers(); fetchRoles(); }, []);

  const handleAdd = () => { form.resetFields(); setModalOpen(true); };

  const openEdit = (record) => {
    setEditModal({ open:true, user:record });
  };

  // 编辑弹窗打开时同步表单初始值
  useEffect(() => {
    if (editModal.open && editModal.user) {
      const statusVal = Number(editModal.user.status ?? editModal.user.is_active ?? 0);
      const roleList = Array.isArray(editModal.user.roles)
        ? editModal.user.roles
        : (typeof editModal.user.roles === 'string' ? editModal.user.roles.split(',').map(r => r.trim()).filter(Boolean) : []);
      editForm.setFieldsValue({
        username: editModal.user.username,
        name: editModal.user.name,
        status: statusVal === 1,
        roles: roleList
      });
      setEditRoles(roleList);
      setEditStatus(statusVal === 1);
    }
  }, [editModal, editForm]);
  const handleEditOk = async () => {
    try {
      const values = await editForm.validateFields();
      const payload = {
        name: values.name,
        status: (typeof editStatus === 'boolean' ? editStatus : values.status) ? 1 : 0,
        roles: Array.isArray(editRoles) && editRoles.length ? editRoles : (values.roles || [])
      };
      if(values.password){ payload.password = values.password; }
      const res = await window.api.patch('/users/'+editModal.user.id, payload);
      if(!res.data.success) throw new Error(res.data.error||'更新失败');
      message.success('已更新');
      setEditModal({ open:false, user:null });
      fetchUsers();
    } catch(e){ message.error(e.message); }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const res = await window.api.post('/users', values);
      if (!res.data.success) throw new Error(res.data.error || '创建失败');
      message.success('用户已创建');
      setModalOpen(false);
      fetchUsers();
    } catch (e) {
      message.error(e.message);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '用户名', dataIndex: 'username' },
    { title: '姓名', dataIndex: 'name' },
    { title: '角色', dataIndex: 'roles', render: roles => {
      const arr = Array.isArray(roles)
        ? roles
        : (typeof roles === 'string' ? roles.split(',').map(r => r.trim()).filter(Boolean) : []);
      return arr.map(r => <Tag key={r}>{r}</Tag>);
    } },
    { title: '状态', dataIndex: 'status', render: (v, record) => {
      const val = Number(v ?? record?.is_active ?? 0);
      return val === 1 ? <Tag color='green'>启用</Tag> : <Tag color='red'>禁用</Tag>;
    } },
    { title: '操作', render:(_,r)=> <Space>
      <Button size='small' onClick={()=>openEdit(r)}>编辑</Button>
      <Button size='small' danger onClick={()=>handleDelete(r)}>删除</Button>
    </Space> }
  ];

  const grouped = useMemo(() => {
    const map = new Map();
    for (const u of users || []) {
      const dept = String(u?.department || '').trim() || '未分配部门';
      if (!map.has(dept)) map.set(dept, []);
      map.get(dept).push(u);
    }
    const depts = Array.from(map.keys()).sort((a, b) => {
      if (a === '未分配部门') return 1;
      if (b === '未分配部门') return -1;
      return a.localeCompare(b, 'zh-CN');
    });
    return depts.map((dept) => ({ dept, users: map.get(dept) || [] }));
  }, [users]);


  // 删除用户
  const handleDelete = (user) => {
    Modal.confirm({
      title: `确认删除用户 #${user.id} (${user.username})?`,
      content: '此操作不可恢复，确定要删除该用户吗？',
      okText: '删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const res = await window.api.delete('/users/' + user.id);
          if (!res.data.success) throw new Error(res.data.error || '删除失败');
          message.success('用户已删除');
          fetchUsers();
        } catch (e) {
          message.error(e.message);
        }
      }
    });
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button type='primary' onClick={handleAdd}>新增用户</Button>
      </Space>

      {grouped.map((g) => (
        <div key={g.dept} style={{ marginBottom: 18 }}>
          <div style={{ margin: '8px 0 10px', fontWeight: 600 }}>
            {g.dept}（{g.users.length}）
          </div>
          <Table
            rowKey='id'
            columns={columns}
            dataSource={g.users}
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        </div>
      ))}
      <Modal open={modalOpen} title='新增用户' onCancel={() => setModalOpen(false)} onOk={handleOk} destroyOnClose>
        <Form form={form} layout='vertical'>
          <Form.Item label='用户名' name='username' rules={[{ required: true, message: '必填' }]}><Input /></Form.Item>
          <Form.Item label='姓名' name='name'><Input /></Form.Item>
          <Form.Item label='密码' name='password' rules={[{ required: true, message: '必填' }]}><Input.Password /></Form.Item>
          <Form.Item label='角色' name='roles' rules={[{ required: true, message: '必选' }]}>
            <Select mode='multiple' options={rolesOptions} placeholder='选择角色' />
          </Form.Item>
        </Form>
      </Modal>
      <Modal open={editModal.open} title={'编辑用户 #'+editModal.user?.id} onCancel={()=> setEditModal({ open:false, user:null })} onOk={handleEditOk} destroyOnClose>
        <Form form={editForm} layout='vertical'>
          <Form.Item label='用户名' name='username'>
            <Input disabled />
          </Form.Item>
          <Form.Item label='姓名' name='name'><Input /></Form.Item>
          <Form.Item label='角色' name='roles' rules={[{ required:true, message:'必选' }]}> <Select mode='multiple' options={rolesOptions} value={editRoles} onChange={setEditRoles} /> </Form.Item>
          <Form.Item label='启用状态' name='status' valuePropName='checked'> <Switch checked={editStatus} onChange={setEditStatus} /> </Form.Item>
          <Form.Item label='新密码(留空则不改)' name='password'> <Input.Password autoComplete='new-password' /> </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default UserManagementPage;
